import { useEffect, useMemo, useRef, useState } from "react";
import { formatUzPhone, initialPhoneInput, isBlankPhoneInput } from "../phone";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  Copy,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
  UserSearch,
  Users as UsersIcon,
  Wallet,
} from "lucide-react";
import {
  api,
  type CarWithHistory,
  type ClientDetails,
  type ClientOverviewRow,
  type ClientSort,
  type ManualMessageRow,
} from "../api";
import { useFeature } from "../lib/use-tariff";
import { useBranch } from "../lib/use-branch";
import { fmtDate, fmtDateTime, fmtInt, fmtMoney, toMoneyRaw } from "../lib/format";
import { MoneyInput } from "../components/money-input";
import { DebtPaymentDialog } from "../components/debt-payment-dialog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { PageFilterPopover } from "../components/page-filter-popover";
import { usePageHeader } from "../lib/use-page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Money, Plate, Mono, StatusChip } from "../components/ui/ledger";

const PAGE_SIZE = 20;

/** Saralanadigan ustun sarlavhasi — bosilganda yo'nalishni almashtiradi. */
function SortHead({
  k,
  sort,
  order,
  onSort,
  children,
  align = "left",
  title,
}: {
  k: ClientSort;
  sort: ClientSort;
  order: "asc" | "desc";
  onSort: (k: ClientSort) => void;
  children: React.ReactNode;
  align?: "left" | "right";
  title?: string;
}) {
  const active = sort === k;
  return (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        title={title}
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          active ? "text-foreground font-semibold" : ""
        }`}
      >
        {children}
        {active &&
          (order === "desc" ? (
            <ArrowDown className="size-3" />
          ) : (
            <ArrowUp className="size-3" />
          ))}
      </button>
    </TableHead>
  );
}

/** Kartochkadagi bitta metrika katagi. */
function Stat({
  label,
  value,
  hint,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  sub?: string;
}) {
  return (
    <div className="space-y-1" title={hint}>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-lg">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

/**
 * Pul rangi. Aylanma uchun: musbat — neytral, manfiy (qaytarish xariddan ko'p)
 * — qizil. Balans uchun (`isBalance`): musbat = mijoz QARZDOR (qizil), manfiy =
 * mijozda kredit (yashil) — `Qarzlar` sahifasidagi mantiq bilan bir xil.
 */
function moneyTone(
  value: string | undefined,
  isBalance = false,
): "default" | "pos" | "neg" {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n === 0) return "default";
  if (isBalance) return n > 0 ? "neg" : "pos";
  return n < 0 ? "neg" : "default";
}

// "Qancha vaqtdan beri kelmadi" filtri — oy sifatida ko'rsatiladi, serverga
// kun bo'lib ketadi (backend `not_seen_days`).
const LAPSED_MONTHS = [3, 6, 12] as const;

export function ClientsPage() {
  const { t } = useTranslation("clients");
  const [q, setQ] = useState("");
  const [onlyDisconnected, setOnlyDisconnected] = useState(false);
  const [notSeenMonths, setNotSeenMonths] = useState<number | null>(null);
  const [sort, setSort] = useState<ClientSort>("last_seen");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState<ClientOverviewRow[]>([]);
  const [selected, setSelected] = useState<ClientDetails | null>(null);
  const [editing, setEditing] = useState<ClientDetails | null>(null);
  // Qarz to'lovi dialogi — mijoz kartochkasidan ochiladi.
  const [paying, setPaying] = useState<ClientDetails | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [botUsername, setBotUsername] = useState<string>("");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const canMessage = useFeature("manual_messaging");
  // Qarz daftari yo'q tarifda kartochkadagi balans UMUMAN chizilmaydi — backend
  // ham maydonni yubormaydi.
  const hasDebts = useFeature("qarzlar");
  const [messaging, setMessaging] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgTemplate, setMsgTemplate] = useState("free");
  const [sending, setSending] = useState(false);
  const [msgHistory, setMsgHistory] = useState<ManualMessageRow[]>([]);
  // Global filial selektori (topbar) — "shu filialda tashrifi bor mijozlar".
  const { selectedBranchId } = useBranch();

  // Input darhol yangilanadi, server qidiruvi esa qisqa pauzadan keyin ishlaydi.
  // Shu tariqa har bir klavish uchun alohida so'rov yuborilmaydi.
  const [debouncedQ, setDebouncedQ] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  async function load(p: number) {
    setErr(null);
    setLoading(true);
    try {
      const res = await api.clientsOverview({
        q: debouncedQ.trim() || undefined,
        has_telegram: onlyDisconnected ? false : undefined,
        not_seen_days:
          notSeenMonths == null ? undefined : Math.round(notSeenMonths * 30),
        branch_id: selectedBranchId ?? undefined,
        sort,
        order,
        page: p,
        page_size: PAGE_SIZE,
      });
      setResults(res.items);
      setTotal(res.total);
    } catch (e) {
      setErr((e as { message?: string }).message || t("error"));
    } finally {
      setLoading(false);
    }
  }

  // Kartochka ro'yxat qatoridan ko'ra ko'proq ma'lumot talab qiladi (mashinalar
  // + tarix), shuning uchun tanlanganda alohida o'qiladi.
  async function openClient(id: number) {
    try {
      setSelected(await api.clientDetails(id));
    } catch (e) {
      toast.error((e as { message?: string }).message || t("error"));
    }
  }

  // Tahrirlash formasi mashinalar bilan ishlaydi — ro'yxat qatorida ular yo'q,
  // shuning uchun avval to'liq kartochka o'qiladi.
  async function editRow(id: number) {
    try {
      setEditing(await api.clientDetails(id));
    } catch (e) {
      toast.error((e as { message?: string }).message || t("error"));
    }
  }

  useEffect(() => {
    api
      .botInfo()
      .then((b) => setBotUsername(b.username || ""))
      .catch(() => {});
  }, []);

  // YAGONA yuklash effekti — filtr, saralash va sahifa bittada. Bir nechta
  // `set*` React'da bitta render'ga birlashadi, ya'ni saralashni o'zgartirish
  // (u sahifani ham 1 ga qaytaradi) ikki marta so'rov yubormaydi.
  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyDisconnected, notSeenMonths, sort, order, page, debouncedQ, reloadKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q), 350);
    return () => window.clearTimeout(timer);
  }, [q]);

  // Filial topbar'dan (kontekstdan) o'zgaradi, o'z handler'imiz yo'q — shuning
  // uchun sahifani 1 ga qaytarish alohida effektda. `setPage`+`setReloadKey`
  // bitta render'ga birlashadi → yuqoridagi effekt BIR marta ishlaydi.
  const branchRef = useRef(selectedBranchId);
  useEffect(() => {
    if (branchRef.current === selectedBranchId) return;
    branchRef.current = selectedBranchId;
    setPage(1);
    setReloadKey((k) => k + 1);
  }, [selectedBranchId]);

  // Mijoz/mashina o'zgargach ro'yxatni va ochiq kartochkani yangilab olamiz —
  // mashinalar saqlangan yangi holatda ko'rinishi uchun serverdan qayta o'qiymiz.
  async function reload(selectedId?: number) {
    setReloadKey((k) => k + 1);
    const id = selectedId ?? selected?.id;
    if (id != null) {
      try {
        setSelected(await api.clientDetails(id));
      } catch {
        setSelected(null);
      }
    }
  }

  // Ustun sarlavhasini bosish: o'sha ustun bo'lsa yo'nalishni almashtiradi,
  // aks holda yangi ustunga o'tadi (pul/sana uchun avval kamayish tartibida).
  function toggleSort(key: ClientSort) {
    setPage(1);
    if (sort === key) {
      setOrder((o) => (o === "desc" ? "asc" : "desc"));
    } else {
      setSort(key);
      setOrder(key === "name" ? "asc" : "desc");
    }
  }

  // Qidiruv endi ochiq panelda (popover'da emas) — badge faqat popover
  // filtrlarini sanaydi; qidiruv matnining holati o'zi ko'rinib turadi.
  const filterCount = (onlyDisconnected ? 1 : 0) + (notSeenMonths != null ? 1 : 0);
  const clearFilters = () => {
    setOnlyDisconnected(false);
    setNotSeenMonths(null);
    setPage(1);
  };
  // "Topilmadi" vs "Mijozlar yo'q": qidiruv, popover filtri yoki filial
  // torayishi bor bo'lsa — birinchisi.
  const narrowed = filterCount > 0 || q.trim() !== "" || selectedBranchId != null;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyDisconnected}
              onChange={(e) => {
                setPage(1);
                setOnlyDisconnected(e.target.checked);
              }}
              className="size-4 rounded border-input accent-primary"
            />
            <span>{t("search.onlyDisconnected")}</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="not-seen">{t("search.notSeenLabel")}</Label>
            <select
              id="not-seen"
              value={notSeenMonths ?? ""}
              onChange={(e) => {
                setPage(1);
                setNotSeenMonths(e.target.value ? Number(e.target.value) : null);
              }}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">{t("search.notSeenAny")}</option>
              {LAPSED_MONTHS.map((m) => (
                <option key={m} value={m}>
                  {t("search.notSeenMonths", { count: m })}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {t("search.notSeenHint")}
            </p>
          </div>
        </div>
      </PageFilterPopover>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlyDisconnected, notSeenMonths, filterCount, t],
  );

  const actionsNode = useMemo(
    () => (
      <Button size="sm" onClick={() => setCreating(true)}>
        <Plus />
        {t("add.button")}
      </Button>
    ),
    [t],
  );

  usePageHeader({ filters: filterNode, actions: actionsNode, filterCount });

  function copyLink(clientId: number) {
    if (!botUsername) return;
    const url = `https://t.me/${botUsername}?start=client_${clientId}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedId(clientId);
        toast.success(t("link.copySuccess"));
        setTimeout(
          () => setCopiedId((id) => (id === clientId ? null : id)),
          2000,
        );
      })
      .catch(() => toast.error(t("link.copyError")));
  }

  // Ochilgan mijozning qo'lda xabar tarixini yuklaymiz (feature + ulangan bo'lsa).
  useEffect(() => {
    if (!selected || !canMessage || selected.telegram_id == null) {
      setMsgHistory([]);
      return;
    }
    api
      .manualMessageHistory(selected.id)
      .then(setMsgHistory)
      .catch(() => setMsgHistory([]));
  }, [selected, canMessage]);

  function fillTemplate(key: string) {
    setMsgTemplate(key);
    if (key === "free") {
      setMsgText("");
      return;
    }
    const name = selected?.full_name || "";
    const plate = selected?.cars[0]?.plate_number || "";
    const tpl = t(`messaging.templates.${key}`, { name, plate });
    setMsgText(tpl);
  }

  function openMessaging() {
    setMsgTemplate("free");
    setMsgText("");
    setMessaging(true);
  }

  async function sendMessage() {
    if (!selected) return;
    const text = msgText.trim();
    if (!text) {
      toast.error(t("messaging.empty"));
      return;
    }
    setSending(true);
    try {
      const res = await api.sendManualMessage(selected.id, {
        text,
        send_uuid: crypto.randomUUID(),
        template_key: msgTemplate === "free" ? undefined : msgTemplate,
      });
      if (res.blocked) {
        toast.error(t("messaging.blocked"));
      } else if (res.status === "sent") {
        toast.success(t("messaging.sent"));
      } else {
        toast.error(t("messaging.failed"));
      }
      setMessaging(false);
      try {
        setMsgHistory(await api.manualMessageHistory(selected.id));
      } catch {
        /* tarixni yangilay olmadik — jim o'tamiz */
      }
    } catch (e) {
      toast.error((e as { message?: string }).message || t("messaging.failed"));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="clients-page space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Ochiq qidiruv — ism/telefon/davlat raqami bittada, popover'siz. */}
      {!selected && (
        <div className="clients-toolbar flex items-center gap-2">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder={t("search.placeholder")}
              aria-label={t("search.label")}
              className="pl-8"
            />
          </div>
        </div>
      )}

      {!selected && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : results.length === 0 ? (
              <EmptyState
                icon={<UserSearch />}
                title={narrowed ? t("empty.notFound") : t("empty.noClients")}
                description={t("empty.description")}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead k="name" sort={sort} order={order} onSort={toggleSort}>
                      {t("table.client")}
                    </SortHead>
                    <TableHead>{t("table.phone")}</TableHead>
                    <TableHead>{t("table.cars")}</TableHead>
                    <SortHead
                      k="visits"
                      sort={sort}
                      order={order}
                      onSort={toggleSort}
                      align="right"
                      title={t("metrics.visitsHint")}
                    >
                      {t("metrics.visits")}
                    </SortHead>
                    <SortHead
                      k="last_seen"
                      sort={sort}
                      order={order}
                      onSort={toggleSort}
                    >
                      {t("metrics.lastSeen")}
                    </SortHead>
                    <TableHead className="text-right">{t("table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((c) => {
                    const connected = c.telegram_id != null;
                    return (
                      <TableRow
                        key={c.id}
                        onClick={() => void openClient(c.id)}
                        className="cursor-pointer"
                      >
                        <TableCell className="font-medium">
                          <div>{c.full_name}</div>
                          {!connected && (
                            <span className="text-xs text-muted-foreground">
                              {t("bot.disconnected")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Mono>{formatUzPhone(c.phone)}</Mono>
                        </TableCell>
                        <TableCell>
                          {c.plates.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {c.plates.map((p) => (
                                <Plate key={p}>{p}</Plate>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Mono>{fmtInt(c.visits)}</Mono>
                        </TableCell>
                        <TableCell>
                          {c.last_seen ? (
                            <Mono className="text-muted-foreground">
                              {fmtDate(c.last_seen)}
                            </Mono>
                          ) : (
                            <span className="text-muted-foreground">
                              {t("metrics.never")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="client-actions text-right whitespace-nowrap">
                          {!connected && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!botUsername}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyLink(c.id);
                              }}
                            >
                              {copiedId === c.id ? <Check /> : <Copy />}
                              {copiedId === c.id ? t("link.copied") : t("link.copy")}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              void editRow(c.id);
                            }}
                          >
                            <Pencil />
                            {t("actions.edit")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
          {!loading && results.length > 0 && pageCount > 1 && (
            <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("pager.total", { count: total })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("pager.prev")}
                </Button>
                <span className="tabular-nums text-muted-foreground">
                  {page} / {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  {t("pager.next")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {selected && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xl font-bold tracking-tight">
                {selected.full_name}
              </h2>
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                <Mono>{formatUzPhone(selected.phone)}</Mono>
                <span>·</span>
                {selected.telegram_id != null ? (
                  <StatusChip tone="ok">{t("bot.connectedFull")}</StatusChip>
                ) : (
                  <StatusChip tone="warn">{t("bot.disconnectedFull")}</StatusChip>
                )}
                {selected.telegram_id == null && botUsername && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyLink(selected.id)}
                  >
                    {copiedId === selected.id ? <Check /> : <Copy />}
                    {copiedId === selected.id ? t("link.copied") : t("link.copy")}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Qarz to'lovi — faqat qarz daftari bor tarifda. Balans
                  `metrics.balans` da (u ham shu bayroq bilan kelmaydi). */}
              {hasDebts && selected.metrics?.balans !== undefined && (
                <Button variant="outline" onClick={() => setPaying(selected)}>
                  <Wallet />
                  {t("actions.payment")}
                </Button>
              )}
              {canMessage && selected.telegram_id != null && (
                <Button onClick={openMessaging}>
                  <MessageSquare />
                  {t("messaging.openButton")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setEditing(selected)}>
                <Pencil />
                {t("actions.edit")}
              </Button>
              <Button variant="outline" onClick={() => setSelected(null)}>
                <ArrowLeft />
                {t("actions.back")}
              </Button>
            </div>
          </div>

          {selected.metrics && (
            <Card>
              <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-5">
                <Stat
                  label={t("metrics.volume")}
                  hint={t("metrics.volumeHint")}
                  value={
                    <Money tone={moneyTone(selected.metrics.volume)}>
                      {fmtMoney(selected.metrics.volume)}
                    </Money>
                  }
                />
                <Stat
                  label={t("metrics.avg")}
                  hint={t("metrics.avgHint")}
                  value={
                    <Mono>{fmtMoney(selected.metrics.avg_transaction)}</Mono>
                  }
                  sub={t("metrics.visitsValue", {
                    count: selected.metrics.visits,
                  })}
                />
                <Stat
                  label={t("metrics.since")}
                  hint={t("metrics.sinceHint")}
                  value={<Mono>{fmtDate(selected.metrics.customer_since)}</Mono>}
                />
                <Stat
                  label={t("metrics.lastSeen")}
                  value={
                    selected.metrics.last_seen ? (
                      <Mono>{fmtDate(selected.metrics.last_seen)}</Mono>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("metrics.never")}
                      </span>
                    )
                  }
                />
                {/* Balans — faqat qarz daftari bor tarifda. Backend maydonni
                    yubormasa ustun umuman chizilmaydi (bo'sh/nol emas). */}
                {selected.metrics.balans !== undefined && (
                  <Stat
                    label={t("metrics.balance")}
                    hint={t("metrics.balanceHint")}
                    value={
                      <Money tone={moneyTone(selected.metrics.balans, true)}>
                        {fmtMoney(selected.metrics.balans)}
                      </Money>
                    }
                    sub={
                      Number(selected.metrics.balans) < 0
                        ? t("metrics.balanceCredit")
                        : undefined
                    }
                  />
                )}
              </CardContent>
            </Card>
          )}

          {selected.cars.length === 0 ? (
            <EmptyState
              icon={<UsersIcon />}
              title={t("cars.emptyTitle")}
              description={t("cars.emptyDescription")}
            />
          ) : (
            selected.cars.map((car) => (
              <Card key={car.id}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="flex items-center gap-3">
                      <Plate className="text-sm">{car.plate_number}</Plate>
                      <span className="text-sm font-normal text-muted-foreground">
                        {car.model || ""} {car.year || ""}
                      </span>
                    </CardTitle>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>
                        {t("cars.current")} <strong className="tabular-nums text-foreground">{fmtInt(car.km)}</strong>
                      </span>
                      <span>·</span>
                      <span>
                        {t("cars.nextDue")} {car.next_due_at ? fmtDate(car.next_due_at) : "—"}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {car.history.length === 0 ? (
                    <EmptyState
                      title={t("cars.noHistoryTitle")}
                      description=""
                      className="py-8"
                    />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("history.date")}</TableHead>
                          <TableHead>{t("history.service")}</TableHead>
                          <TableHead>{t("history.master")}</TableHead>
                          <TableHead className="text-right">{t("history.km")}</TableHead>
                          <TableHead className="text-right">{t("history.price")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {car.history.map((h) => (
                          <TableRow key={h.work_id}>
                            <TableCell>
                              <Mono className="text-muted-foreground">
                                {fmtDateTime(h.created_at)}
                              </Mono>
                            </TableCell>
                            <TableCell>{h.service_name}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {h.master_name}
                            </TableCell>
                            <TableCell className="text-right">
                              <Mono>{fmtInt(h.km)}</Mono>
                            </TableCell>
                            <TableCell className="text-right">
                              <Money tone="pos">{fmtMoney(h.price)}</Money>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            ))
          )}

          {canMessage && selected.telegram_id != null && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("messaging.historyTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {msgHistory.length === 0 ? (
                  <EmptyState
                    title={t("messaging.historyEmpty")}
                    description=""
                    className="py-8"
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("history.date")}</TableHead>
                        <TableHead>{t("messaging.textLabel")}</TableHead>
                        <TableHead>{t("history.master")}</TableHead>
                        <TableHead className="text-right">
                          {t("table.status")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {msgHistory.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>
                            <Mono className="text-muted-foreground">
                              {fmtDateTime(m.created_at)}
                            </Mono>
                          </TableCell>
                          <TableCell className="max-w-[24rem] truncate">
                            {m.text}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {m.user_name || "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <StatusChip tone={m.blocked ? "warn" : "ok"}>
                              {m.blocked
                                ? t("messaging.statusFailed")
                                : t("messaging.statusSent")}
                            </StatusChip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={messaging} onOpenChange={(o) => !o && setMessaging(false)}>
        {selected && (
          <>
            <DialogHeader>
              <DialogTitle>{t("messaging.title")}</DialogTitle>
              <DialogDescription>
                {selected.full_name} · <Mono>{formatUzPhone(selected.phone)}</Mono>
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="msg-template">
                  {t("messaging.templateLabel")}
                </Label>
                <select
                  id="msg-template"
                  value={msgTemplate}
                  onChange={(e) => fillTemplate(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="free">{t("messaging.templateFree")}</option>
                  <option value="ready">
                    {t("messaging.templates.ready", { name: "…", plate: "…" })}
                  </option>
                  <option value="part">
                    {t("messaging.templates.part", { name: "…", plate: "…" })}
                  </option>
                  <option value="thanks">
                    {t("messaging.templates.thanks", { name: "…", plate: "…" })}
                  </option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msg-text">{t("messaging.textLabel")}</Label>
                <textarea
                  id="msg-text"
                  value={msgText}
                  maxLength={1000}
                  rows={4}
                  onChange={(e) => setMsgText(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {t("messaging.charCount", { count: msgText.length })}
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setMessaging(false)}
                disabled={sending}
              >
                {t("actions.back")}
              </Button>
              <Button onClick={sendMessage} disabled={sending || !msgText.trim()}>
                <Send />
                {sending ? t("messaging.sending") : t("messaging.send")}
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <ClientForm
            item={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              const id = editing.id;
              setEditing(null);
              void reload(id);
            }}
          />
        )}
      </Dialog>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        {creating && (
          <NewClientForm
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              void reload();
            }}
          />
        )}
      </Dialog>

      {/* Qarz to'lovi — `Qarzlar` sahifasidagi bilan AYNI dialog va ayni
          endpoint; to'lovdan keyin kartochka qayta o'qiladi, shunda balans
          darhol yangilanadi. */}
      {paying && (
        <DebtPaymentDialog
          target={{ kind: "client", clientId: paying.id, name: paying.full_name }}
          balans={paying.metrics?.balans ?? "0"}
          onClose={() => setPaying(null)}
          onSaved={() => {
            const id = paying.id;
            setPaying(null);
            void openClient(id);
            void reload();
          }}
        />
      )}
    </div>
  );
}

// Forma ichidagi mashina qatori — mavjud (id bor) yoki yangi (id null).
type CarDraft = {
  id: number | null;
  plate_number: string;
  model: string;
  km: string; // MoneyInput uchun xom butun satr
};

function emptyCar(): CarDraft {
  return { id: null, plate_number: "", model: "", km: "" };
}

function carToDraft(c: CarWithHistory): CarDraft {
  return {
    id: c.id,
    plate_number: c.plate_number,
    model: c.model ?? "",
    km: toMoneyRaw(String(c.km)),
  };
}

/** Mashina maydonlari (qo'shish/tahrirlash formalarida birga ishlatiladi). */
function CarFieldset({
  cars,
  setCars,
  busy,
}: {
  cars: CarDraft[];
  setCars: React.Dispatch<React.SetStateAction<CarDraft[]>>;
  busy: boolean;
}) {
  const { t } = useTranslation(["clients", "common"]);
  const update = (i: number, patch: Partial<CarDraft>) =>
    setCars((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => setCars((cs) => cs.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{t("clients:form.carsSection")}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCars((cs) => [...cs, emptyCar()])}
          disabled={busy}
        >
          <Plus />
          {t("clients:form.addCar")}
        </Button>
      </div>
      {cars.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("clients:cars.emptyDescription")}</p>
      ) : (
        cars.map((car, i) => (
          <div key={car.id ?? `new-${i}`} className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("clients:form.carsSection")} {i + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                disabled={busy}
              >
                <Trash2 />
                {t("clients:form.removeCar")}
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>{t("clients:form.plateLabel")}</Label>
                <Input
                  value={car.plate_number}
                  onChange={(e) => update(i, { plate_number: e.target.value.toUpperCase() })}
                  placeholder={t("clients:form.platePlaceholder")}
                  disabled={busy}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("clients:form.modelLabel")}</Label>
                <Input
                  value={car.model}
                  onChange={(e) => update(i, { model: e.target.value })}
                  placeholder={t("clients:form.modelPlaceholder")}
                  disabled={busy}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("clients:form.kmLabel")}</Label>
                <MoneyInput
                  value={car.km}
                  onChange={(km) => update(i, { km })}
                  placeholder={t("clients:form.kmPlaceholder")}
                  className="text-right"
                  disabled={busy}
                />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function carPayload(car: CarDraft) {
  return {
    plate_number: car.plate_number.trim().toUpperCase(),
    model: car.model.trim() || null,
    km: Number(car.km) || 0,
  };
}

function ClientForm({
  item,
  onCancel,
  onSaved,
}: {
  item: ClientDetails;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["clients", "common"]);
  const [fullName, setFullName] = useState(item.full_name);
  const [phone, setPhone] = useState(initialPhoneInput(item.phone));
  const [cars, setCars] = useState<CarDraft[]>(item.cars.map(carToDraft));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (fullName.trim() === "" || isBlankPhoneInput(phone)) {
      setErr(t("clients:form.nameValidation"));
      return;
    }
    if (cars.some((c) => c.plate_number.trim() === "")) {
      setErr(t("clients:form.plateValidation"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.updateClient(item.id, {
        full_name: fullName.trim(),
        phone: phone.trim(),
      });
      // Formadan olib tashlangan mashinalarni o'chiramiz.
      const keptIds = new Set(
        cars.filter((c) => c.id != null).map((c) => c.id as number),
      );
      for (const old of item.cars) {
        if (!keptIds.has(old.id)) await api.deleteCar(old.id);
      }
      // Yangi mashinalarni yaratamiz, mavjudlarini yangilaymiz.
      for (const car of cars) {
        if (car.id == null) await api.createCar({ client_id: item.id, ...carPayload(car) });
        else await api.updateCar(car.id, carPayload(car));
      }
      toast.success(t("clients:edit.updated"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("clients:edit.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{t("clients:edit.title")}</DialogTitle>
        <DialogDescription>{t("clients:edit.desc")}</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="cl-name">{t("clients:form.nameLabel")}</Label>
          <Input
            id="cl-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cl-phone">{t("clients:form.phoneLabel")}</Label>
          <Input
            id="cl-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+998901234567"
            required
            disabled={busy}
          />
        </div>
        <CarFieldset cars={cars} setCars={setCars} busy={busy} />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("common:actions.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function NewClientForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["clients", "common"]);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("+998");
  const [cars, setCars] = useState<CarDraft[]>([emptyCar()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (fullName.trim() === "" || isBlankPhoneInput(phone)) {
      setErr(t("clients:form.nameValidation"));
      return;
    }
    if (cars.some((c) => c.plate_number.trim() === "")) {
      setErr(t("clients:form.plateValidation"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const client = await api.createClient({
        full_name: fullName.trim(),
        phone: phone.trim(),
      });
      for (const car of cars) {
        await api.createCar({ client_id: client.id, ...carPayload(car) });
      }
      toast.success(t("clients:add.created"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("clients:edit.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{t("clients:add.title")}</DialogTitle>
        <DialogDescription>{t("clients:add.desc")}</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="ncl-name">{t("clients:form.nameLabel")}</Label>
          <Input
            id="ncl-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ncl-phone">{t("clients:form.phoneLabel")}</Label>
          <Input
            id="ncl-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+998901234567"
            required
            disabled={busy}
          />
        </div>
        <CarFieldset cars={cars} setCars={setCars} busy={busy} />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("common:actions.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
